import {
  pgTable,
  text,
  varchar,
  timestamp,
  jsonb,
  index,
  uniqueIndex,
  serial,
  boolean,
  pgEnum,
  integer,
  numeric,
  date,
  primaryKey,
  type AnyPgColumn,
} from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Session storage table - required for Replit Auth
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

// User authentication types
export const userAuthTypeEnum = pgEnum("user_auth_type", [
  "replit", // Replit OpenID Connect users
  "internal" // Internal users with password authentication
]);

// User storage table - supports multiple authentication methods
export const users = pgTable("users", {
  id: varchar("id").primaryKey().notNull(),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  role: varchar("role").notNull().default("direct_client"), // admin, staff, service_bureau, direct_client
  companyId: integer("company_id").references((): AnyPgColumn => companies.id),
  
  // Authentication fields
  authType: userAuthTypeEnum("auth_type").notNull().default("replit"),
  replitId: varchar("replit_id").unique(), // Actual Replit user ID - populated on first login for pre-authorized users
  passwordHash: varchar("password_hash"), // Only for internal users
  tempPassword: boolean("temp_password").default(false), // Forces password change on first login
  lastLogin: timestamp("last_login"),
  isActive: boolean("is_active").notNull().default(true), // Deactivated users cannot log in
  
  // Password reset fields
  passwordResetToken: varchar("password_reset_token"),
  passwordResetExpires: timestamp("password_reset_expires"),
  
  // Invite token fields (for new user welcome emails)
  inviteToken: varchar("invite_token"), // Hashed token for secure invite links
  inviteTokenExpires: timestamp("invite_token_expires"), // 72-hour expiration
  inviteSentAt: timestamp("invite_sent_at"), // Track when invite was sent
  inviteAcceptedAt: timestamp("invite_accepted_at"), // Track when user accepted invite
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references((): AnyPgColumn => users.id),
  updatedBy: varchar("updated_by").references((): AnyPgColumn => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references((): AnyPgColumn => users.id),
}, (table) => [
  // Partial indexes for token lookups — avoids full table scan on every token validation.
  // PRD-04 v2 §S4.5: run EXPLAIN after db:push to confirm index scans are used.
  index("idx_users_password_reset_token").on(table.passwordResetToken).where(sql`password_reset_token IS NOT NULL`),
  index("idx_users_invite_token").on(table.inviteToken).where(sql`invite_token IS NOT NULL`),
]);

// Contacts table - for people we need to track (not necessarily app users)
export const contacts = pgTable("contacts", {
  id: serial("id").primaryKey(),
  firstName: varchar("first_name").notNull(),
  lastName: varchar("last_name").notNull(),
  email: varchar("email"),
  phone: varchar("phone"),
  mobilePhone: varchar("mobile_phone"),
  jobTitle: varchar("job_title"),
  department: varchar("department"),
  
  // Note: contactType and isPrimary removed - these are now managed per-company via contact_companies table
  // The relationshipType in contact_companies designates the contact's role for that specific company
  // The isPrimary boolean in contact_companies designates the primary contact for invoicing/work orders
  
  notes: text("notes"),
  isActive: boolean("is_active").default(true),
  
  // Optional link to user if this contact becomes an app user
  userId: varchar("user_id").references(() => users.id),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
}, (table) => [
  index("idx_contacts_cursor").on(table.createdAt, table.id),
]);

// Company types enum
export const companyTypeEnum = pgEnum("company_type", ["service_bureau", "direct_client"]);

// Company status enum
export const companyStatusEnum = pgEnum("company_status", ["inquiry", "lead", "client", "blocked"]);

// Login.gov authentication method enum
export const loginGovAuthMethodEnum = pgEnum("login_gov_auth_method", [
  "face_touch_unlock",
  "authentication_application", 
  "security_key",
  "text_call",
  "backup_code",
  "piv_cac",
  "none",
  "unknown"
]);

// Docket prefix enum for carriers
export const docketPrefixEnum = pgEnum("docket_prefix", ["MC", "FF", "MX"]);

// Billing preference enum for companies
export const billingPreferenceEnum = pgEnum("billing_preference", ["pay_at_service", "batch"]);

// Batch interval enum for companies
export const batchIntervalEnum = pgEnum("batch_interval", ["weekly", "biweekly", "monthly"]);

// Companies table
export const companies = pgTable("companies", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull(),
  type: companyTypeEnum("type").notNull(),
  ein: varchar("ein"),
  status: companyStatusEnum("status").notNull().default("inquiry"),
  statusManuallyOverridden: boolean("status_manually_overridden").notNull().default(false),
  inactiveDotNumbers: text("inactive_dot_numbers"),
  dotPin: varchar("dot_pin"),
  loginGovUsername: varchar("login_gov_username"),
  loginGovAuthMethod: loginGovAuthMethodEnum("login_gov_auth_method"),
  email: varchar("email"),
  phone: varchar("phone"),
  address: text("address"),
  mailingAddress: text("mailing_address"),
  
  billingPreference: billingPreferenceEnum("billing_preference").default("pay_at_service"),
  batchInterval: batchIntervalEnum("batch_interval"),
  stripeCustomerId: varchar("stripe_customer_id"),
  nextBatchRunAt: timestamp("next_batch_run_at"),
  billingEmail: varchar("billing_email"),
  billingEmailSameAsCompany: boolean("billing_email_same_as_company").default(true),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references((): AnyPgColumn => users.id),
  updatedBy: varchar("updated_by").references((): AnyPgColumn => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references((): AnyPgColumn => users.id),
}, (table) => [
  index("idx_companies_cursor").on(table.createdAt, table.id),
]);

// Company Credentials table - stores sensitive DOT PIN and login.gov credentials
export const companyCredentials = pgTable("company_credentials", {
  id: serial("id").primaryKey(),
  companyId: integer("company_id").notNull().references(() => companies.id).unique(),
  
  // DOT PIN (alphanumeric)
  dotPin: varchar("dot_pin"),
  
  // Login.gov credentials
  loginGovUsername: varchar("login_gov_username"),
  loginGovAuthMethod: loginGovAuthMethodEnum("login_gov_auth_method"),
  
  // Audit fields
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
});

// Carriers table
export const carriers = pgTable("carriers", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull(),
  
  // Docket information (renamed from mcNumber)
  docketPrefix: docketPrefixEnum("docket_prefix").notNull(),
  docketNumber: varchar("docket_number"),
  
  // Contact information
  email: varchar("email"),
  phone: varchar("phone"),
  address: text("address"),
  mailingAddress: text("mailing_address"),
  
  // FMCSA fields
  legalName: varchar("legal_name"),
  dbaName: varchar("dba_name"),
  
  // Authority status fields
  commonAuthorityStatus: varchar("common_authority_status"), // NONE, ACTIVE, etc.
  contractAuthorityStatus: varchar("contract_authority_status"),
  brokerAuthorityStatus: varchar("broker_authority_status"),
  
  // Pending application/revocation flags
  commonApplicationPending: boolean("common_application_pending"),
  contractApplicationPending: boolean("contract_application_pending"),
  brokerApplicationPending: boolean("broker_application_pending"),
  commonRevocationPending: boolean("common_revocation_pending"),
  contractRevocationPending: boolean("contract_revocation_pending"),
  brokerRevocationPending: boolean("broker_revocation_pending"),
  
  // Operating authority fields
  propertyCarriage: boolean("property_carriage"),
  passengerCarriage: boolean("passenger_carriage"),
  householdGoodsCarriage: boolean("household_goods_carriage"),
  privateCarriage: boolean("private_carriage"),
  enterpriseCarriage: boolean("enterprise_carriage"),
  
  // Insurance fields
  bipdRequired: varchar("bipd_required"),
  bipdOnFile: varchar("bipd_on_file"),
  cargoRequired: varchar("cargo_required"),
  cargoOnFile: varchar("cargo_on_file"),
  bondRequired: varchar("bond_required"),
  bondOnFile: varchar("bond_on_file"),
  
  // Mail delivery status
  undeliverableMail: boolean("undeliverable_mail"),

  // FMCSA data refresh tracking
  lastFmcsaFetchedAt: timestamp("last_fmcsa_fetched_at"),
  
  companyId: integer("company_id").references(() => companies.id).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
}, (table) => [
  index("idx_carriers_company_id").on(table.companyId),
  index("idx_carriers_cursor").on(table.createdAt, table.id),
]);

// Service types table for dynamic service management
export const serviceTypes = pgTable("service_types", {
  id: varchar("id").primaryKey(), // e.g., "mc_application"
  name: varchar("name").notNull(),
  description: text("description").notNull(),
  category: varchar("category").notNull(), // Authority, Registration, Business Formation, etc.
  requiredFor: varchar("required_for").notNull().default(""), // Required for field (legacy, can be empty)
  estimatedDays: varchar("estimated_days").notNull(),
  requirements: text("requirements").array().notNull().default([]), // List of requirements for this service type
  relatedServiceTypes: varchar("related_service_types").array().default([]), // Auto-create these service types when this one is created
  sopUrl: varchar("sop_url"), // Optional link to standard operating procedure document
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
});

// Reporting buckets (Earl's three-way sales grouping) live in CODE, not the
// database: shared/reportingBuckets.ts maps each service_types.id to a bucket.
// Deliberate choice 2026-08-29 — Ben prefers editing one reviewed file over
// running database changes on Replit. Reassignment reclassifies all history.

// Credential Types table - master data for credential types managed in Admin
export const credentialTypes = pgTable("credential_types", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull(),
  description: text("description"),
  agency: varchar("agency"), // Issuing agency (e.g., FMCSA, DOT, State DMV)
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
});

// Junction table for Service Types to Credential Types (many-to-many)
export const serviceTypeCredentials = pgTable("service_type_credentials", {
  id: serial("id").primaryKey(),
  serviceTypeId: varchar("service_type_id").references(() => serviceTypes.id, { onDelete: "cascade" }).notNull(),
  credentialTypeId: integer("credential_type_id").references(() => credentialTypes.id, { onDelete: "cascade" }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
}, (table) => [
  index("idx_service_type_credentials_service_type_id").on(table.serviceTypeId),
  index("idx_service_type_credentials_credential_type_id").on(table.credentialTypeId),
]);

// FMCSA Incidents table
export const fmcsaIncidents = pgTable("fmcsa_incidents", {
  id: serial("id").primaryKey(),
  incidentNumber: varchar("incident_number").notNull().unique(),
  status: varchar("status").notNull().default("Unresolved"), // Unresolved, Resolved
  notes: text("notes"),
  companyId: integer("company_id").references(() => companies.id),
  expedited: boolean("expedited").notNull().default(false),
  expeditedAt: timestamp("expedited_at"),
  expeditedBy: varchar("expedited_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
}, (table) => [
  index("idx_fmcsa_incidents_company_id").on(table.companyId),
  index("idx_fmcsa_incidents_cursor").on(table.createdAt, table.id),
]);

// Junction table for incidents and work orders (many-to-many)
export const incidentWorkOrders = pgTable("incident_work_orders", {
  id: serial("id").primaryKey(),
  incidentId: integer("incident_id").notNull().references(() => fmcsaIncidents.id, { onDelete: "cascade" }),
  workOrderId: integer("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_incident_work_orders_incident_id").on(table.incidentId),
  index("idx_incident_work_orders_work_order_id").on(table.workOrderId),
]);

// Work order status enum
export const workOrderStatusEnum = pgEnum("work_order_status", [
  "pending",
  "in_progress",
  "awaiting_documents",
  "completed",
  "cancelled",
  "new",
  "collecting_inputs",
  "validating",
  "awaiting_payment",
  "preparing_documents",
  "awaiting_signatures",
  "submitted",
  "awaiting_client_filing",
  "work_complete",
  "closed",
]);

// Work orders table
export const workOrders = pgTable("work_orders", {
  id: serial("id").primaryKey(),
  workOrderNumber: varchar("work_order_number").notNull().unique(),
  contactId: integer("contact_id").references(() => contacts.id).notNull(),
  companyId: integer("company_id").references(() => companies.id).notNull(),
  carrierId: integer("carrier_id").references(() => carriers.id), // Now optional
  secondaryContactId: integer("secondary_contact_id").references(() => contacts.id), // Optional secondary contact
  serviceType: varchar("service_type").references(() => serviceTypes.id).notNull(), // References service_types table
  serviceRateId: integer("service_rate_id").references(() => serviceRates.id), // Link to applicable service rate
  status: workOrderStatusEnum("status").notNull().default("new"),
  description: text("description"),
  assignedToUserId: varchar("assigned_to_user_id").references(() => users.id),
  estimatedCompletionDate: timestamp("estimated_completion_date"),
  completedDate: timestamp("completed_date"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
}, (table) => [
  index("idx_work_orders_company_id").on(table.companyId),
  index("idx_work_orders_contact_id").on(table.contactId),
  index("idx_work_orders_carrier_id").on(table.carrierId),
  index("idx_work_orders_assigned_to_user_id").on(table.assignedToUserId),
  index("idx_work_orders_status").on(table.status),
  index("idx_work_orders_service_type").on(table.serviceType),
  index("idx_work_orders_cursor").on(table.createdAt, table.id),
]);

// Work order status history table
export const workOrderStatusHistory = pgTable("work_order_status_history", {
  id: serial("id").primaryKey(),
  workOrderId: integer("work_order_id").references(() => workOrders.id, { onDelete: "cascade" }).notNull(),
  fromStatus: workOrderStatusEnum("from_status"),
  toStatus: workOrderStatusEnum("to_status").notNull(),
  changedBy: varchar("changed_by").references(() => users.id),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
  notes: text("notes"),
}, (table) => [
  index("idx_wo_status_history_work_order_id").on(table.workOrderId),
  index("idx_wo_status_history_changed_at").on(table.changedAt),
]);

export type WorkOrderStatusHistory = typeof workOrderStatusHistory.$inferSelect;
export type InsertWorkOrderStatusHistory = typeof workOrderStatusHistory.$inferInsert;

// Task templates table - atomic reusable tasks
export const taskTemplates = pgTable("task_templates", {
  id: serial("id").primaryKey(),
  taskName: varchar("task_name").notNull(),
  taskDescription: text("task_description"),
  targetDuration: varchar("target_duration"), // e.g., "2 hours", "1 day"
  formName: varchar("form_name"),
  url: varchar("url"), // Link to SOP document or reference
  isActive: boolean("is_active").default(true),
  tags: text("tags").array().default([]), // Categorization tags
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by").references(() => users.id),
});

// Service templates table - ordered compositions of task templates, 1:1 with service types
export const serviceTemplates = pgTable("service_templates", {
  id: serial("id").primaryKey(),
  serviceTypeId: varchar("service_type_id").references(() => serviceTypes.id).notNull().unique(), // 1:1 relationship
  name: varchar("name").notNull(),
  description: text("description"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
});

// Service template items - junction table linking service templates to task templates with ordering
export const serviceTemplateItems = pgTable("service_template_items", {
  id: serial("id").primaryKey(),
  serviceTemplateId: integer("service_template_id").references(() => serviceTemplates.id, { onDelete: "cascade" }).notNull(),
  taskTemplateId: integer("task_template_id").references(() => taskTemplates.id).notNull(),
  sequenceNumber: integer("sequence_number").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_service_template_items_template_id").on(table.serviceTemplateId),
  index("idx_service_template_items_task_template_id").on(table.taskTemplateId),
]);

// Tasks table for work orders
export const tasks = pgTable("tasks", {
  id: serial("id").primaryKey(),
  // onDelete: restrict — WOs are soft-deleted in practice. restrict makes accidental
  // hard-deletes fail loudly; cascade would silently delete child tasks. (PRD-04 v2 D1)
  workOrderId: integer("work_order_id").references(() => workOrders.id, { onDelete: "restrict" }).notNull(),
  title: varchar("title").notNull(),
  description: text("description"),
  completed: boolean("completed").default(false),
  completedDate: timestamp("completed_date"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
}, (table) => [
  index("idx_tasks_work_order_id").on(table.workOrderId),
]);

// Document requests table
export const documentRequests = pgTable("document_requests", {
  id: serial("id").primaryKey(),
  // onDelete: restrict — WOs are soft-deleted in practice. restrict makes accidental
  // hard-deletes fail loudly; cascade would silently delete child document requests. (PRD-04 v2 D2)
  workOrderId: integer("work_order_id").references(() => workOrders.id, { onDelete: "restrict" }).notNull(),
  documentType: varchar("document_type").notNull(),
  description: text("description"),
  required: boolean("required").default(true),
  submitted: boolean("submitted").default(false),
  submittedDate: timestamp("submitted_date"),
  requestedDate: timestamp("requested_date").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
}, (table) => [
  index("idx_document_requests_work_order_id").on(table.workOrderId),
]);

// Contact-Company relationship types (role of contact for that company)
// Note: "primary" removed - use isPrimary boolean on contact_companies instead
export const contactCompanyRelationshipEnum = pgEnum("contact_company_relationship", [
  "billing", 
  "operations",
  "safety",
  "dispatch",
  "compliance",
  "emergency",
  "owner",
  "other"
]);

// Junction table for contact-company many-to-many relationships
export const contactCompanies = pgTable("contact_companies", {
  id: serial("id").primaryKey(),
  contactId: integer("contact_id").references(() => contacts.id).notNull(),
  companyId: integer("company_id").references(() => companies.id).notNull(),
  relationshipType: contactCompanyRelationshipEnum("relationship_type").notNull().default("other"),
  isPrimary: boolean("is_primary").default(false), // Only one primary relationship per contact
  notes: text("notes"), // Specific notes about this relationship
  isActive: boolean("is_active").default(true),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
}, (table) => [
  index("idx_contact_companies_contact_id").on(table.contactId),
  index("idx_contact_companies_company_id").on(table.companyId),
]);

// Service rates table
export const serviceRates = pgTable("service_rates", {
  id: serial("id").primaryKey(),
  serviceTypeId: varchar("service_type_id").references(() => serviceTypes.id).notNull(),
  rate: numeric("rate", { precision: 10, scale: 2 }).notNull(), // USD amount with 2 decimal places
  directCost: numeric("direct_cost", { precision: 10, scale: 2 }), // Optional: cost to provide this service (for reporting only)
  effectiveDate: date("effective_date").notNull(), // Date this rate becomes effective
  isActive: boolean("is_active").default(true),
  
  stripeProductId: varchar("stripe_product_id"),
  stripePriceId: varchar("stripe_price_id"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
});

// Company-specific service rates (custom rates that override defaults)
export const companyServiceRates = pgTable("company_service_rates", {
  id: serial("id").primaryKey(),
  serviceTypeId: varchar("service_type_id").references(() => serviceTypes.id).notNull(),
  rate: numeric("rate", { precision: 10, scale: 2 }).notNull(), // USD amount with 2 decimal places
  directCost: numeric("direct_cost", { precision: 10, scale: 2 }), // Optional: cost to provide this service (for reporting only)
  effectiveStartDate: date("effective_start_date").notNull(), // Date this rate becomes effective
  effectiveEndDate: date("effective_end_date"), // Nullable - null means rate is valid until changed
  isActive: boolean("is_active").default(true),
  description: text("description"), // Optional description/notes for this custom rate
  
  stripeProductId: varchar("stripe_product_id"),
  stripePriceId: varchar("stripe_price_id"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
});

// Junction table linking multiple companies to a single custom rate
export const companyServiceRateCompanies = pgTable("company_service_rate_companies", {
  id: serial("id").primaryKey(),
  companyServiceRateId: integer("company_service_rate_id").references(() => companyServiceRates.id).notNull(),
  companyId: integer("company_id").references(() => companies.id).notNull(),
  
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
}, (table) => [
  index("idx_company_service_rate_companies_rate_id").on(table.companyServiceRateId),
  index("idx_company_service_rate_companies_company_id").on(table.companyId),
]);

// Invoice line item type enum
export const invoiceLineTypeEnum = pgEnum("invoice_line_type", [
  "service_fee",    // Service rate fee
  "pass_through"    // Pass-through fee (government/jurisdiction fees)
]);

// Charge type enum for invoice line items
export const chargeTypeEnum = pgEnum("charge_type", [
  "include_in_invoice",      // Include in Stripe invoice
  "paid_with_client_card",   // Paid directly by client (e.g., to state/federal jurisdictions)
  "not_applicable"           // Not applicable (quantity should be 0)
]);

// Pass-through fees table
export const passThroughFees = pgTable("pass_through_fees", {
  id: serial("id").primaryKey(),
  description: varchar("description").notNull(),
  amount: numeric("amount", { precision: 10, scale: 2 }), // Nullable - some fees vary and are set per work order
  effectiveDate: date("effective_date").notNull(),
  endDate: date("end_date"), // Nullable - null means no end date
  stripeProductId: varchar("stripe_product_id"), // For future Stripe integration
  internalNotes: text("internal_notes"), // Internal guidance notes for staff
  defaultChargeType: chargeTypeEnum("default_charge_type").notNull().default("include_in_invoice"),
  isActive: boolean("is_active").default(true),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
});

// Junction table linking pass-through fees to multiple service types
export const passThroughFeeServiceTypes = pgTable("pass_through_fee_service_types", {
  id: serial("id").primaryKey(),
  passThroughFeeId: integer("pass_through_fee_id").references(() => passThroughFees.id, { onDelete: "cascade" }).notNull(),
  serviceTypeId: varchar("service_type_id").references(() => serviceTypes.id).notNull(),
  
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
}, (table) => [
  index("idx_pass_through_fee_service_types_fee_id").on(table.passThroughFeeId),
  index("idx_pass_through_fee_service_types_service_type_id").on(table.serviceTypeId),
]);

// Work order invoice line items - tracks service fees and pass-through fees per work order
export const workOrderInvoiceLineItems = pgTable("work_order_invoice_line_items", {
  id: serial("id").primaryKey(),
  workOrderId: integer("work_order_id").references(() => workOrders.id, { onDelete: "cascade" }).notNull(),
  
  lineType: invoiceLineTypeEnum("line_type").notNull(),
  referenceId: integer("reference_id"), // Points to serviceRates.id or passThroughFees.id based on lineType
  // Documents which table referenceId points to. NOT enforced by pgEnum (PRD-04 v2 D4c/D5).
  // Null on rows predating PRD-04; new writes set this explicitly.
  // TypeScript union: InvoiceLineReferenceType = "service_rate" | "pass_through_fee"
  referenceType: varchar("reference_type"),
  
  // Snapshot fields - captured at creation for historical accuracy
  description: varchar("description").notNull(),
  unitAmount: numeric("unit_amount", { precision: 10, scale: 2 }), // Nullable for variable fees
  quantity: integer("quantity").notNull().default(1),
  
  // Charge type - determines how this line item is billed
  chargeType: chargeTypeEnum("charge_type").notNull().default("include_in_invoice"),
  
  // Stripe fields for future integration
  stripePriceId: varchar("stripe_price_id"),
  
  // Client comments - can be shared on invoice
  clientComments: text("client_comments"),
  
  // Staff notes from the pass-through fee record (snapshot at creation)
  staffNotes: text("staff_notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
}, (table) => [
  index("idx_work_order_invoice_line_items_work_order_id").on(table.workOrderId),
]);

// Polymorphic notes table
export const notes = pgTable("notes", {
  id: serial("id").primaryKey(),
  content: text("content").notNull(),
  authorId: varchar("author_id").references(() => users.id).notNull(),
  entityType: varchar("entity_type").notNull(), // 'task', 'work_order', 'carrier', 'company', 'contact'
  entityId: varchar("entity_id").notNull(), // The ID of the related entity
  isPrivate: boolean("is_private").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  // Audit + soft-delete fields added in PRD-04 v2 §S4.3.
  // Backfill: UPDATE notes SET created_by = author_id WHERE created_by IS NULL;
  createdBy: varchar("created_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by").references(() => users.id),
}, (table) => [
  index("idx_notes_entity").on(table.entityType, table.entityId),
  index("idx_notes_author_id").on(table.authorId),
]);

// Audit trail table for tracking all changes
export const auditTrail = pgTable("audit_trail", {
  id: serial("id").primaryKey(),
  entityType: varchar("entity_type").notNull(), // 'user', 'company', 'carrier', 'work_order', 'task', 'contact', 'document_request', 'note'
  entityId: varchar("entity_id").notNull(), // The ID of the related entity
  action: varchar("action").notNull(), // 'create', 'update', 'delete', 'restore'
  fieldName: varchar("field_name"), // specific field that was changed (null for create/delete)
  oldValue: text("old_value"), // previous value (null for create)
  newValue: text("new_value"), // new value (null for delete)
  userId: varchar("user_id").references(() => users.id).notNull(), // who made the change
  timestamp: timestamp("timestamp").defaultNow(),
  ipAddress: varchar("ip_address"),
  userAgent: varchar("user_agent"),
}, (table) => [
  index("idx_audit_trail_entity").on(table.entityType, table.entityId),
  index("idx_audit_trail_user_id").on(table.userId),
  index("idx_audit_trail_timestamp").on(table.timestamp),
]);

// Work order payment status enum
export const paymentStatusEnum = pgEnum("payment_status", [
  "draft",
  "pending",
  "paid",
  "partial",
  "overdue",
  "voided",
  "uncollectible"
]);

// Work order payments table - tracks Stripe invoice/payment status for each work order
export const workOrderPayments = pgTable("work_order_payments", {
  id: serial("id").primaryKey(),
  workOrderId: integer("work_order_id").references(() => workOrders.id, { onDelete: "cascade" }).notNull(),
  batchInvoiceRunId: integer("batch_invoice_run_id"), // Links to batch invoice run if part of a batch
  stripeInvoiceId: varchar("stripe_invoice_id"),
  stripeInvoiceNumber: varchar("stripe_invoice_number"), // Human-readable invoice number from Stripe
  stripePaymentIntentId: varchar("stripe_payment_intent_id"),
  hostedInvoiceUrl: varchar("hosted_invoice_url"),
  pdfUrl: varchar("pdf_url"), // Link to the invoice PDF
  status: paymentStatusEnum("status").notNull().default("draft"),
  amountDue: numeric("amount_due", { precision: 10, scale: 2 }),
  amountPaid: numeric("amount_paid", { precision: 10, scale: 2 }),
  feeTotal: numeric("fee_total", { precision: 10, scale: 2 }),
  unpaidBalance: numeric("unpaid_balance", { precision: 10, scale: 2 }),
  deliveryChannels: text("delivery_channels").array().default([]), // email, sms, etc.
  customerEmail: varchar("customer_email"), // Email the invoice was sent to
  lastSyncedAt: timestamp("last_synced_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_work_order_payments_work_order_id").on(table.workOrderId),
  index("idx_work_order_payments_stripe_invoice_id").on(table.stripeInvoiceId),
  index("idx_work_order_payments_cursor").on(table.createdAt, table.id),
]);

// Batch invoice run status enum
export const batchInvoiceStatusEnum = pgEnum("batch_invoice_status", [
  "pending",
  "processing",
  "completed",
  "failed"
]);

// Batch invoice runs table - tracks batch invoicing for companies with batch billing
export const batchInvoiceRuns = pgTable("batch_invoice_runs", {
  id: serial("id").primaryKey(),
  companyId: integer("company_id").references(() => companies.id).notNull(),
  intervalType: batchIntervalEnum("interval_type").notNull(),
  periodStart: date("period_start").notNull(),
  periodEnd: date("period_end").notNull(),
  stripeInvoiceId: varchar("stripe_invoice_id"),
  status: batchInvoiceStatusEnum("status").notNull().default("pending"),
  triggeredBy: varchar("triggered_by").references(() => users.id),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_batch_invoice_runs_company_id").on(table.companyId),
]);

// Time tracking sessions: passive browser-based tracking of the active time staff
// spend on a work order detail page. One row per page mount (session_token), holding
// the cumulative active milliseconds the browser has reported. Rows are written only
// through timeTrackingStorage.recordTimeTrackingHeartbeat, which upserts atomically.
export const timeTrackingSessions = pgTable("time_tracking_sessions", {
  id: serial("id").primaryKey(),
  workOrderId: integer("work_order_id").references(() => workOrders.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id).notNull(),
  sessionToken: varchar("session_token").notNull(),
  activeMs: integer("active_ms").notNull().default(0),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  lastHeartbeatAt: timestamp("last_heartbeat_at").defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_time_tracking_session_token").on(table.sessionToken),
  // Leading work_order_id also serves per-work-order totals.
  index("idx_time_tracking_wo_user").on(table.workOrderId, table.userId),
]);

// Relations
export const usersRelations = relations(users, ({ one, many }) => ({
  company: one(companies, {
    fields: [users.companyId],
    references: [companies.id],
  }),
  assignedWorkOrders: many(workOrders),
}));

export const contactCompaniesRelations = relations(contactCompanies, ({ one }) => ({
  contact: one(contacts, {
    fields: [contactCompanies.contactId],
    references: [contacts.id],
  }),
  company: one(companies, {
    fields: [contactCompanies.companyId],
    references: [companies.id],
  }),
}));

// DOT Profiles table - normalized storage for DOT profile data
export const dotProfiles = pgTable("dot_profiles", {
  id: serial("id").primaryKey(),
  companyId: integer("company_id").notNull().references(() => companies.id).unique(),
  dotNumber: varchar("dot_number").notNull(),
  dbaName: varchar("dba_name"),
  carrierOperation: varchar("carrier_operation"),
  classDefinition: varchar("class_definition"),
  carship: varchar("carship"),
  powerUnits: varchar("power_units"),
  truckUnits: varchar("truck_units"),
  busUnits: varchar("bus_units"),
  totalDrivers: varchar("total_drivers"),
  totalCdl: varchar("total_cdl"),
  driverInterTotal: varchar("driver_inter_total"),
  fleetSize: varchar("fleet_size"),
  hmIndicator: varchar("hm_indicator"),
  mcs150Date: varchar("mcs150_date"),
  mcs150Mileage: varchar("mcs150_mileage"),
  mcs150MileageYear: varchar("mcs150_mileage_year"),
  addDate: varchar("add_date"),
  interstateBeyond100Miles: varchar("interstate_beyond_100_miles"),
  interstateWithin100Miles: varchar("interstate_within_100_miles"),
  intrastateBeyond100Miles: varchar("intrastate_beyond_100_miles"),
  intrastateWithin100Miles: varchar("intrastate_within_100_miles"),
  businessOrgDescription: varchar("business_org_description"),
  cargoGenFreight: varchar("cargo_gen_freight"),
  cargoLogPole: varchar("cargo_log_pole"),
  lastFetchedAt: timestamp("last_fetched_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// DOT Fetch History table - audit log of every DOT API fetch
export const dotFetchHistory = pgTable("dot_fetch_history", {
  id: serial("id").primaryKey(),
  dotProfileId: integer("dot_profile_id").notNull().references(() => dotProfiles.id),
  dotNumber: varchar("dot_number").notNull(),
  fetchedBy: varchar("fetched_by").references(() => users.id),
  rawResponse: jsonb("raw_response"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_dot_fetch_history_dot_profile_id").on(table.dotProfileId),
]);

export const companiesRelations = relations(companies, ({ one, many }) => ({
  users: many(users),
  carriers: many(carriers),
  contactCompanies: many(contactCompanies),
  companyServiceRateCompanies: many(companyServiceRateCompanies),
  dotProfile: one(dotProfiles, {
    fields: [companies.id],
    references: [dotProfiles.companyId],
  }),
}));

export const dotProfilesRelations = relations(dotProfiles, ({ one, many }) => ({
  company: one(companies, {
    fields: [dotProfiles.companyId],
    references: [companies.id],
  }),
  fetchHistory: many(dotFetchHistory),
}));

export const dotFetchHistoryRelations = relations(dotFetchHistory, ({ one }) => ({
  dotProfile: one(dotProfiles, {
    fields: [dotFetchHistory.dotProfileId],
    references: [dotProfiles.id],
  }),
  fetchedByUser: one(users, {
    fields: [dotFetchHistory.fetchedBy],
    references: [users.id],
  }),
}));



export const carriersRelations = relations(carriers, ({ one, many }) => ({
  company: one(companies, {
    fields: [carriers.companyId],
    references: [companies.id],
  }),
  workOrders: many(workOrders),
}));

export const serviceRatesRelations = relations(serviceRates, ({ one, many }) => ({
  serviceType: one(serviceTypes, {
    fields: [serviceRates.serviceTypeId],
    references: [serviceTypes.id],
  }),
  workOrders: many(workOrders),
}));

export const companyServiceRatesRelations = relations(companyServiceRates, ({ one, many }) => ({
  serviceType: one(serviceTypes, {
    fields: [companyServiceRates.serviceTypeId],
    references: [serviceTypes.id],
  }),
  companyServiceRateCompanies: many(companyServiceRateCompanies),
}));

export const companyServiceRateCompaniesRelations = relations(companyServiceRateCompanies, ({ one }) => ({
  companyServiceRate: one(companyServiceRates, {
    fields: [companyServiceRateCompanies.companyServiceRateId],
    references: [companyServiceRates.id],
  }),
  company: one(companies, {
    fields: [companyServiceRateCompanies.companyId],
    references: [companies.id],
  }),
}));

export const serviceTypesRelations = relations(serviceTypes, ({ many, one }) => ({
  serviceRates: many(serviceRates),
  companyServiceRates: many(companyServiceRates),
  serviceTemplate: one(serviceTemplates), // 1:1 relationship
  passThroughFeeServiceTypes: many(passThroughFeeServiceTypes),
}));

export const passThroughFeesRelations = relations(passThroughFees, ({ many }) => ({
  passThroughFeeServiceTypes: many(passThroughFeeServiceTypes),
}));

export const passThroughFeeServiceTypesRelations = relations(passThroughFeeServiceTypes, ({ one }) => ({
  passThroughFee: one(passThroughFees, {
    fields: [passThroughFeeServiceTypes.passThroughFeeId],
    references: [passThroughFees.id],
  }),
  serviceType: one(serviceTypes, {
    fields: [passThroughFeeServiceTypes.serviceTypeId],
    references: [serviceTypes.id],
  }),
}));

export const workOrderInvoiceLineItemsRelations = relations(workOrderInvoiceLineItems, ({ one }) => ({
  workOrder: one(workOrders, {
    fields: [workOrderInvoiceLineItems.workOrderId],
    references: [workOrders.id],
  }),
}));

export const serviceTemplatesRelations = relations(serviceTemplates, ({ one, many }) => ({
  serviceType: one(serviceTypes, {
    fields: [serviceTemplates.serviceTypeId],
    references: [serviceTypes.id],
  }),
  serviceTemplateItems: many(serviceTemplateItems),
}));

export const serviceTemplateItemsRelations = relations(serviceTemplateItems, ({ one }) => ({
  serviceTemplate: one(serviceTemplates, {
    fields: [serviceTemplateItems.serviceTemplateId],
    references: [serviceTemplates.id],
  }),
  taskTemplate: one(taskTemplates, {
    fields: [serviceTemplateItems.taskTemplateId],
    references: [taskTemplates.id],
  }),
}));

export const taskTemplatesRelations = relations(taskTemplates, ({ many }) => ({
  serviceTemplateItems: many(serviceTemplateItems),
}));

export const workOrdersRelations = relations(workOrders, ({ one, many }) => ({
  contact: one(contacts, {
    fields: [workOrders.contactId],
    references: [contacts.id],
  }),
  company: one(companies, {
    fields: [workOrders.companyId],
    references: [companies.id],
  }),
  carrier: one(carriers, {
    fields: [workOrders.carrierId],
    references: [carriers.id],
  }),
  assignedToUser: one(users, {
    fields: [workOrders.assignedToUserId],
    references: [users.id],
  }),
  serviceRate: one(serviceRates, {
    fields: [workOrders.serviceRateId],
    references: [serviceRates.id],
  }),
  tasks: many(tasks),
  invoiceLineItems: many(workOrderInvoiceLineItems),
  documentRequests: many(documentRequests),
}));

export const tasksRelations = relations(tasks, ({ one }) => ({
  workOrder: one(workOrders, {
    fields: [tasks.workOrderId],
    references: [workOrders.id],
  }),
}));

export const documentRequestsRelations = relations(documentRequests, ({ one }) => ({
  workOrder: one(workOrders, {
    fields: [documentRequests.workOrderId],
    references: [workOrders.id],
  }),
}));

export const notesRelations = relations(notes, ({ one }) => ({
  author: one(users, {
    fields: [notes.authorId],
    references: [users.id],
  }),
}));

export const contactsRelations = relations(contacts, ({ one, many }) => ({
  user: one(users, {
    fields: [contacts.userId],
    references: [users.id],
  }),
  contactCompanies: many(contactCompanies),
}));

export const auditTrailRelations = relations(auditTrail, ({ one }) => ({
  user: one(users, {
    fields: [auditTrail.userId],
    references: [users.id],
  }),
}));

export const fmcsaIncidentRelations = relations(fmcsaIncidents, ({ one, many }) => ({
  company: one(companies, {
    fields: [fmcsaIncidents.companyId],
    references: [companies.id],
  }),
  createdByUser: one(users, {
    fields: [fmcsaIncidents.createdBy],
    references: [users.id],
    relationName: "fmcsaIncidentCreatedBy",
  }),
  updatedByUser: one(users, {
    fields: [fmcsaIncidents.updatedBy],
    references: [users.id],
    relationName: "fmcsaIncidentUpdatedBy",
  }),
  expeditedByUser: one(users, {
    fields: [fmcsaIncidents.expeditedBy],
    references: [users.id],
    relationName: "fmcsaIncidentExpeditedBy",
  }),
  incidentWorkOrders: many(incidentWorkOrders),
}));

export const incidentWorkOrderRelations = relations(incidentWorkOrders, ({ one }) => ({
  incident: one(fmcsaIncidents, {
    fields: [incidentWorkOrders.incidentId],
    references: [fmcsaIncidents.id],
  }),
  workOrder: one(workOrders, {
    fields: [incidentWorkOrders.workOrderId],
    references: [workOrders.id],
  }),
}));

// Enums for new tables
export const watchlistStatusEnum = pgEnum("watchlist_status", [
  "active",
  "matched",
  "completed",
  "expired",
  "canceled"
]);

export const watchlistTypeEnum = pgEnum("watchlist_type", [
  "single",
  "multiple"
]);

export const watchlistTargetTypeEnum = pgEnum("watchlist_target_type", [
  "dot",
  "docket"
]);

export const notificationTypeEnum = pgEnum("notification_type", [
  "watchlist_matched",
  "watchlist_completed",
  "watchlist_expired",
  "watchlist_canceled",
  "watchlist_updated",
  "system"
]);

// Email status enum for authority letter notifications
export const letterEmailStatusEnum = pgEnum("letter_email_status", [
  "pending",        // Letter exists but not yet ready to send (service date in future)
  "scheduled",      // Ready to be sent on next scheduled run
  "sent",           // Email successfully sent
  "failed",         // Email failed after all retry attempts
  "blocked_test"    // Blocked due to test mode domain filtering
]);

// Authority Letters table - stores FMCSA authority letter metadata from Helix API
export const authorityLetters = pgTable("authority_letters", {
  id: serial("id").primaryKey(),
  workOrderId: integer("work_order_id").references(() => workOrders.id).notNull(),
  watchlistId: integer("watchlist_id"),
  
  // Related company and carrier (derived from work order)
  companyId: integer("company_id").references(() => companies.id),
  carrierId: integer("carrier_id").references(() => carriers.id),
  
  // Helix API document data
  helixDocumentId: integer("helix_document_id").notNull(),
  documentIdentifier: varchar("document_identifier").notNull(),
  docketPrefix: varchar("docket_prefix", { length: 10 }),
  docketNumber: integer("docket_number"),
  docketSuffix: varchar("docket_suffix", { length: 10 }),
  docketFull: varchar("docket_full"),
  usdotNumber: integer("usdot_number"),
  
  // Document metadata
  documentType: varchar("document_type"),
  letterType: varchar("letter_type"),
  letterCode: varchar("letter_code"),
  authorityType: varchar("authority_type"),
  
  // Company information
  companyName: varchar("company_name"),
  dbaName: varchar("dba_name"),
  cityState: varchar("city_state"),
  
  // Dates
  serviceDate: date("service_date"),
  requestDate: date("request_date"),
  decidedDate: date("decided_date"),
  processedAt: timestamp("processed_at"),
  
  // Download link
  downloadUrl: varchar("download_url"),
  remotePath: varchar("remote_path"),
  
  // Email notification tracking
  emailStatus: letterEmailStatusEnum("email_status").default("pending"),
  emailSentAt: timestamp("email_sent_at"),
  emailError: text("email_error"),
  emailRetryCount: integer("email_retry_count").default(0),
  emailNextRetryAt: timestamp("email_next_retry_at"),
  emailBatchId: varchar("email_batch_id"), // Groups letters sent in same email
  
  // Local PDF storage path (for email attachments)
  localPdfPath: varchar("local_pdf_path"),
  
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
}, (table) => [
  index("idx_authority_letters_work_order_id").on(table.workOrderId),
  index("idx_authority_letters_company_id").on(table.companyId),
  index("idx_authority_letters_carrier_id").on(table.carrierId),
  index("idx_authority_letters_email_status").on(table.emailStatus),
  index("idx_authority_letters_cursor").on(table.createdAt, table.id),
]);

// Authority Letter Email Logs - audit trail for email sends
export const authorityLetterEmailLogs = pgTable("authority_letter_email_logs", {
  id: serial("id").primaryKey(),
  letterId: integer("letter_id").references(() => authorityLetters.id).notNull(),
  workOrderId: integer("work_order_id").references(() => workOrders.id).notNull(),
  batchId: varchar("batch_id"), // Groups multiple letters sent in one email
  
  // Email details
  recipientEmails: text("recipient_emails").array(),
  subject: varchar("subject"),
  
  // Status tracking
  runType: varchar("run_type", { length: 20 }).notNull(), // 'scheduled' or 'manual'
  status: varchar("status", { length: 20 }).notNull(), // 'sent', 'failed', 'blocked_test'
  errorMessage: text("error_message"),
  
  // Payload snapshot for debugging
  payloadSnapshot: jsonb("payload_snapshot"),
  
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
}, (table) => [
  index("idx_authority_letter_email_logs_letter_id").on(table.letterId),
  index("idx_authority_letter_email_logs_work_order_id").on(table.workOrderId),
]);

// Notification Settings - app-wide configurable settings
export const notificationSettings = pgTable("notification_settings", {
  id: serial("id").primaryKey(),
  settingKey: varchar("setting_key", { length: 100 }).notNull().unique(),
  settingValue: jsonb("setting_value").notNull(),
  description: text("description"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by").references(() => users.id),
});

// Dashboard action item types - what kind of item is being snoozed/dismissed
export const actionItemTypeEnum = pgEnum("action_item_type", [
  "work_order",
  "watchlist",
  "authority_letter",
  "fmcsa_incident",
  "payment",
  "invoice"
]);

// Dashboard action item status - snoozed or dismissed
export const actionItemStatusEnum = pgEnum("action_item_status", [
  "snoozed",
  "dismissed"
]);

// Action Item Statuses table - tracks snooze/dismiss actions on dashboard items
export const actionItemStatuses = pgTable("action_item_statuses", {
  id: serial("id").primaryKey(),
  userId: varchar("user_id").notNull().references(() => users.id),
  itemType: actionItemTypeEnum("item_type").notNull(),
  itemId: integer("item_id").notNull(),
  status: actionItemStatusEnum("status").notNull(),
  snoozedUntil: timestamp("snoozed_until"),
  dismissReason: text("dismiss_reason"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_action_item_user").on(table.userId),
  index("idx_action_item_type_id").on(table.itemType, table.itemId),
]);

// Watchlist table - stores watchlist metadata synced with Helix API
export const watchlists = pgTable("watchlists", {
  id: serial("id").primaryKey(),
  workOrderId: integer("work_order_id").references(() => workOrders.id).notNull(),
  
  // Helix API watch data
  helixWatchId: integer("helix_watch_id").notNull().unique(),
  targetType: watchlistTargetTypeEnum("target_type").notNull(),
  targetIdentifier: varchar("target_identifier").notNull(),
  targetLabel: varchar("target_label"),
  
  // DOT/Docket information
  usdotNumber: integer("usdot_number"),
  docketPrefix: varchar("docket_prefix", { length: 10 }),
  docketNumber: integer("docket_number"),
  docketSuffix: varchar("docket_suffix", { length: 10 }),
  
  // Watch configuration
  watchlistDays: integer("watchlist_days"),
  watchlistType: watchlistTypeEnum("watchlist_type").notNull(),
  status: watchlistStatusEnum("status").notNull().default("active"),
  
  // Filter parameters
  letterType: varchar("letter_type"),
  authorityTypeCode: varchar("authority_type_code"),
  authorityTypes: text("authority_types").array(),
  
  // External reference (stores work order ID for Helix)
  externalReferenceId: varchar("external_reference_id"),
  
  // Match tracking
  lastMatchedAt: timestamp("last_matched_at"),
  matchCount: integer("match_count").default(0),
  completedAt: timestamp("completed_at"),
  canceledAt: timestamp("canceled_at"),
  expiredAt: timestamp("expired_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: varchar("created_by").references(() => users.id),
  updatedBy: varchar("updated_by").references(() => users.id),
}, (table) => [
  index("idx_watchlists_work_order_id").on(table.workOrderId),
  index("idx_watchlists_cursor").on(table.createdAt, table.id),
]);

// Watchlist Email Logs table - tracks email sends for watchlist reminders/dismissals
export const watchlistEmailLogs = pgTable("watchlist_email_logs", {
  id: serial("id").primaryKey(),
  watchlistId: integer("watchlist_id").references(() => watchlists.id).notNull(),
  workOrderId: integer("work_order_id").references(() => workOrders.id).notNull(),

  // reminder = active watchlist nudge, dismissal = canceled/expired notification
  emailType: varchar("email_type", { length: 20 }).notNull(),

  // Email details
  recipientEmails: text("recipient_emails").array(),
  subject: varchar("subject"),

  // Status tracking
  status: varchar("status", { length: 20 }).notNull(), // sent | failed | blocked_test
  errorMessage: text("error_message"),

  createdAt: timestamp("created_at").defaultNow(),
  sentBy: varchar("sent_by").references(() => users.id),
}, (table) => [
  index("idx_watchlist_email_logs_watchlist_id").on(table.watchlistId),
  index("idx_watchlist_email_logs_work_order_id").on(table.workOrderId),
]);

// Notifications table - stores in-app notifications
export const notifications = pgTable("notifications", {
  id: serial("id").primaryKey(),
  userId: varchar("user_id").references(() => users.id).notNull(),
  workOrderId: integer("work_order_id").references(() => workOrders.id),
  watchlistId: integer("watchlist_id").references(() => watchlists.id),
  
  type: notificationTypeEnum("type").notNull(),
  title: varchar("title").notNull(),
  message: text("message").notNull(),
  
  // Webhook event data
  eventId: integer("event_id"),
  eventData: jsonb("event_data"),
  
  isRead: boolean("is_read").default(false),
  isDismissed: boolean("is_dismissed").default(false),
  readAt: timestamp("read_at"),
  dismissedAt: timestamp("dismissed_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_notifications_user_id").on(table.userId),
  index("idx_notifications_work_order_id").on(table.workOrderId),
  index("idx_notifications_watchlist_id").on(table.watchlistId),
]);

// Insert schemas
export const insertUserSchema = createInsertSchema(users);
export const createUserSchema = insertUserSchema.omit({ createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertCompanySchema = createInsertSchema(companies).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true, statusManuallyOverridden: true });
export const insertCompanyCredentialsSchema = createInsertSchema(companyCredentials).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true });
export const insertCarrierSchema = createInsertSchema(carriers).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertWorkOrderSchema = createInsertSchema(workOrders).omit({ id: true, workOrderNumber: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertTaskSchema = createInsertSchema(tasks).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertTaskTemplateSchema = createInsertSchema(taskTemplates).omit({ id: true, createdAt: true, updatedAt: true, updatedBy: true });
export const insertServiceTemplateSchema = createInsertSchema(serviceTemplates).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertServiceTemplateItemSchema = createInsertSchema(serviceTemplateItems).omit({ id: true, createdAt: true });
export const insertDocumentRequestSchema = createInsertSchema(documentRequests).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertNoteSchema = createInsertSchema(notes).omit({ id: true, createdAt: true, updatedAt: true });
export const insertContactSchema = createInsertSchema(contacts).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertContactCompanySchema = createInsertSchema(contactCompanies).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertServiceTypeSchema = createInsertSchema(serviceTypes).omit({ createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertCredentialTypeSchema = createInsertSchema(credentialTypes).omit({ id: true, createdAt: true, updatedAt: true, createdBy: true, updatedBy: true, deletedAt: true, deletedBy: true });
export const insertServiceTypeCredentialSchema = createInsertSchema(serviceTypeCredentials).omit({ id: true, createdAt: true, createdBy: true });
export const insertServiceRateSchema = createInsertSchema(serviceRates).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true, 
  createdBy: true, 
  updatedBy: true, 
  deletedAt: true, 
  deletedBy: true 
});
export const insertCompanyServiceRateSchema = createInsertSchema(companyServiceRates).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true, 
  createdBy: true, 
  updatedBy: true, 
  deletedAt: true, 
  deletedBy: true 
});
export const insertCompanyServiceRateCompanySchema = createInsertSchema(companyServiceRateCompanies).omit({ 
  id: true, 
  createdAt: true, 
  createdBy: true
});
export const insertFmcsaIncidentSchema = createInsertSchema(fmcsaIncidents).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true, 
  createdBy: true, 
  updatedBy: true,
  expeditedAt: true,
  expeditedBy: true
}).extend({
  incidentNumber: z.string().min(1, "Incident number is required"),
  companyId: z.number().nullable().optional()
});
export const insertIncidentWorkOrderSchema = createInsertSchema(incidentWorkOrders).omit({ 
  id: true, 
  createdAt: true 
});
export const insertAuditTrailSchema = createInsertSchema(auditTrail).omit({ id: true, timestamp: true });
export const insertAuthorityLetterSchema = createInsertSchema(authorityLetters).omit({ 
  id: true, 
  createdAt: true 
});
export const insertAuthorityLetterEmailLogSchema = createInsertSchema(authorityLetterEmailLogs).omit({
  id: true,
  createdAt: true
});
export const insertWatchlistEmailLogSchema = createInsertSchema(watchlistEmailLogs).omit({
  id: true,
  createdAt: true
});
export const insertNotificationSettingSchema = createInsertSchema(notificationSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
export const insertActionItemStatusSchema = createInsertSchema(actionItemStatuses).omit({
  id: true,
  createdAt: true,
  updatedAt: true
}).extend({
  dismissReason: z.string().min(1, "A reason is required when dismissing an item").optional(),
});
export const insertWatchlistSchema = createInsertSchema(watchlists).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true,
  matchCount: true,
  lastMatchedAt: true
});
export const insertNotificationSchema = createInsertSchema(notifications).omit({ 
  id: true, 
  createdAt: true,
  readAt: true,
  dismissedAt: true
});
export const insertPassThroughFeeSchema = createInsertSchema(passThroughFees).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true, 
  createdBy: true, 
  updatedBy: true, 
  deletedAt: true, 
  deletedBy: true 
});
export const insertPassThroughFeeServiceTypeSchema = createInsertSchema(passThroughFeeServiceTypes).omit({ 
  id: true, 
  createdAt: true, 
  createdBy: true 
});
// unit_amount is numeric(10,2). drizzle-zod types it as a bare string, so any
// text reached Postgres and failed there as a 500 (DOTTIE-BACKEND-1E: "520`").
// Accept a plain decimal with up to 8 whole digits and 2 decimals, or a number.
// An empty string clears the amount (variable fee).
export const INVOICE_AMOUNT_PATTERN = /^-?\d{1,8}(\.\d{1,2})?$/;
export const invoiceAmountSchema = z.preprocess(
  (value) => {
    if (typeof value === "number") return String(value);
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" ? null : trimmed;
    }
    return value;
  },
  z.string()
    .regex(INVOICE_AMOUNT_PATTERN, "Amount must be a number with up to 2 decimal places, like 125 or 125.50")
    .nullable(),
);
export const insertWorkOrderInvoiceLineItemSchema = createInsertSchema(workOrderInvoiceLineItems).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true, 
  createdBy: true, 
  updatedBy: true 
}).extend({
  unitAmount: invoiceAmountSchema.optional(),
});
export const insertWorkOrderPaymentSchema = createInsertSchema(workOrderPayments).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true 
});
export const insertBatchInvoiceRunSchema = createInsertSchema(batchInvoiceRuns).omit({ 
  id: true, 
  createdAt: true 
});
export const insertDotProfileSchema = createInsertSchema(dotProfiles).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true 
});
export const insertDotFetchHistorySchema = createInsertSchema(dotFetchHistory).omit({ 
  id: true, 
  createdAt: true 
});

export const workOrderCounters = pgTable("work_order_counters", {
  year: integer("year").primaryKey(),
  lastValue: integer("last_value").notNull().default(0),
});

export const companyStatusHistory = pgTable("company_status_history", {
  id: serial("id").primaryKey(),
  companyId: integer("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  fromStatus: companyStatusEnum("from_status"),
  toStatus: companyStatusEnum("to_status").notNull(),
  changedByUserId: varchar("changed_by_user_id").references(() => users.id),
  changedAt: timestamp("changed_at").notNull().defaultNow(),
  reason: text("reason").notNull().default("manual"),
});

// Types
export type UpsertUser = z.infer<typeof insertUserSchema>;
export type CreateUser = z.infer<typeof createUserSchema>;
export type User = typeof users.$inferSelect;
export type InsertCompany = z.infer<typeof insertCompanySchema>;
export type Company = typeof companies.$inferSelect;
export type InsertCompanyCredentials = z.infer<typeof insertCompanyCredentialsSchema>;
export type CompanyCredentials = typeof companyCredentials.$inferSelect;
export type CompanyStatusHistory = typeof companyStatusHistory.$inferSelect;
export type InsertCompanyStatusHistory = typeof companyStatusHistory.$inferInsert;
export type InsertCarrier = z.infer<typeof insertCarrierSchema>;
export type Carrier = typeof carriers.$inferSelect;
export type InsertWorkOrder = z.infer<typeof insertWorkOrderSchema>;
export type InsertWorkOrderInternal = InsertWorkOrder & {
  createdBy?: string;
  updatedBy?: string;
};
export type WorkOrder = typeof workOrders.$inferSelect;
export type InsertTask = z.infer<typeof insertTaskSchema>;
export type Task = typeof tasks.$inferSelect;
export type InsertTaskTemplate = z.infer<typeof insertTaskTemplateSchema>;
export type TaskTemplate = typeof taskTemplates.$inferSelect;
export type InsertServiceTemplate = z.infer<typeof insertServiceTemplateSchema>;
export type ServiceTemplate = typeof serviceTemplates.$inferSelect;
export type InsertServiceTemplateItem = z.infer<typeof insertServiceTemplateItemSchema>;
export type ServiceTemplateItem = typeof serviceTemplateItems.$inferSelect;
export type InsertDocumentRequest = z.infer<typeof insertDocumentRequestSchema>;
export type DocumentRequest = typeof documentRequests.$inferSelect;
export type InsertNote = z.infer<typeof insertNoteSchema>;
export type Note = typeof notes.$inferSelect;
export type InsertContact = z.infer<typeof insertContactSchema>;
export type Contact = typeof contacts.$inferSelect;
export type InsertContactCompany = z.infer<typeof insertContactCompanySchema>;
export type ContactCompany = typeof contactCompanies.$inferSelect;
export type InsertServiceType = z.infer<typeof insertServiceTypeSchema>;
export type ServiceType = typeof serviceTypes.$inferSelect;
export type InsertCredentialType = z.infer<typeof insertCredentialTypeSchema>;
export type CredentialType = typeof credentialTypes.$inferSelect;
export type InsertServiceTypeCredential = z.infer<typeof insertServiceTypeCredentialSchema>;
export type ServiceTypeCredential = typeof serviceTypeCredentials.$inferSelect;
export type InsertServiceRate = z.infer<typeof insertServiceRateSchema>;
export type ServiceRate = typeof serviceRates.$inferSelect;
export type InsertCompanyServiceRate = z.infer<typeof insertCompanyServiceRateSchema>;
export type CompanyServiceRate = typeof companyServiceRates.$inferSelect;
export type InsertCompanyServiceRateCompany = z.infer<typeof insertCompanyServiceRateCompanySchema>;
export type CompanyServiceRateCompany = typeof companyServiceRateCompanies.$inferSelect;
export type InsertFmcsaIncident = z.infer<typeof insertFmcsaIncidentSchema>;
export type FmcsaIncident = typeof fmcsaIncidents.$inferSelect;
export type InsertIncidentWorkOrder = z.infer<typeof insertIncidentWorkOrderSchema>;
export type IncidentWorkOrder = typeof incidentWorkOrders.$inferSelect;
export type InsertAuditTrail = z.infer<typeof insertAuditTrailSchema>;
export type AuditTrail = typeof auditTrail.$inferSelect;
export type InsertAuthorityLetter = z.infer<typeof insertAuthorityLetterSchema>;
export type AuthorityLetter = typeof authorityLetters.$inferSelect;
export type InsertAuthorityLetterEmailLog = z.infer<typeof insertAuthorityLetterEmailLogSchema>;
export type AuthorityLetterEmailLog = typeof authorityLetterEmailLogs.$inferSelect;
export type InsertWatchlistEmailLog = z.infer<typeof insertWatchlistEmailLogSchema>;
export type WatchlistEmailLog = typeof watchlistEmailLogs.$inferSelect;
export type InsertNotificationSetting = z.infer<typeof insertNotificationSettingSchema>;
export type NotificationSetting = typeof notificationSettings.$inferSelect;
export type InsertActionItemStatus = z.infer<typeof insertActionItemStatusSchema>;
export type ActionItemStatus = typeof actionItemStatuses.$inferSelect;
export type InsertWatchlist = z.infer<typeof insertWatchlistSchema>;
export type UpdateWatchlistInput = Partial<InsertWatchlist> & {
  matchCount?: number;
  lastMatchedAt?: Date | null;
};
export type Watchlist = typeof watchlists.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;
export type InsertPassThroughFee = z.infer<typeof insertPassThroughFeeSchema>;
export type PassThroughFee = typeof passThroughFees.$inferSelect;
export type InsertPassThroughFeeServiceType = z.infer<typeof insertPassThroughFeeServiceTypeSchema>;
export type PassThroughFeeServiceType = typeof passThroughFeeServiceTypes.$inferSelect;
export type InsertWorkOrderInvoiceLineItem = z.infer<typeof insertWorkOrderInvoiceLineItemSchema>;
export type WorkOrderInvoiceLineItem = typeof workOrderInvoiceLineItems.$inferSelect;
export type InsertWorkOrderPayment = z.infer<typeof insertWorkOrderPaymentSchema>;
export type WorkOrderPayment = typeof workOrderPayments.$inferSelect;
export type InsertDotProfile = z.infer<typeof insertDotProfileSchema>;
export type DotProfile = typeof dotProfiles.$inferSelect;
export type InsertDotFetchHistory = z.infer<typeof insertDotFetchHistorySchema>;
export type DotFetchHistory = typeof dotFetchHistory.$inferSelect;
export type InsertBatchInvoiceRun = z.infer<typeof insertBatchInvoiceRunSchema>;
export type BatchInvoiceRun = typeof batchInvoiceRuns.$inferSelect;
export type TimeTrackingSession = typeof timeTrackingSessions.$inferSelect;

// Extended types with relations
export type NoteWithAuthor = Note & {
  author: User;
};

export type WorkOrderWithRelations = WorkOrder & {
  carrier: CarrierWithRelations | null;
  assignedToUser?: User | null;
  serviceRate?: ServiceRate & { serviceType: ServiceType };
  tasks: Task[];
  documentRequests: DocumentRequest[];
};

export type ServiceRateWithRelations = ServiceRate & {
  serviceType: ServiceType;
};

export type CompanyServiceRateWithRelations = CompanyServiceRate & {
  serviceType: ServiceType;
  companies: Company[];
};

export type TaskTemplateWithRelations = TaskTemplate & {
  serviceType: ServiceType;
};

export type CarrierWithRelations = Carrier & {
  company: (Company & { dotProfile?: DotProfile | null }) | null;
  workOrders: WorkOrder[];
};

export type CompanyWithRelations = Company & {
  users: User[];
  carriers: Carrier[];
  workOrders: WorkOrder[];
  dotProfile?: DotProfile | null;
};

export type DotProfileWithRelations = DotProfile & {
  company: Company;
  fetchHistory: DotFetchHistory[];
};

export type ContactWithUser = Contact & {
  user?: User;
};

export type FmcsaIncidentWithRelations = FmcsaIncident & {
  company?: Company;
  createdByUser?: User;
  updatedByUser?: User;
  expeditedByUser?: Pick<User, "id" | "firstName" | "lastName"> | null;
  workOrders: (WorkOrder & { company: Company })[];
};

export type AuthorityLetterWithRelations = AuthorityLetter & {
  workOrder: WorkOrder;
  createdByUser?: User;
};

export type WatchlistWithRelations = Watchlist & {
  workOrder: WorkOrder;
  createdByUser?: User;
  updatedByUser?: User;
};

export type NotificationWithRelations = Notification & {
  user: User;
  workOrder?: WorkOrder;
  watchlist?: Watchlist;
};

export type PassThroughFeeWithRelations = PassThroughFee & {
  serviceTypes: ServiceType[];
};

export type WorkOrderInvoiceLineItemWithRelations = WorkOrderInvoiceLineItem & {
  workOrder: WorkOrder;
};

// Portal request log status — written before any processing begins
export const portalRequestLogs = pgTable("portal_request_logs", {
  id: serial("id").primaryKey(),
  userId: varchar("user_id").notNull().references(() => users.id),
  submittedAt: timestamp("submitted_at").defaultNow(),
  status: varchar("status").notNull().$type<'pending' | 'success' | 'partial_failure' | 'failed'>().default('pending'),
  requestPayload: jsonb("request_payload").notNull(),
  resultPayload: jsonb("result_payload"),
  errorMessage: text("error_message"),
}, (table) => [
  index("idx_portal_request_logs_user_id").on(table.userId),
]);

export const insertPortalRequestLogSchema = createInsertSchema(portalRequestLogs).omit({
  id: true,
  submittedAt: true,
});
export type InsertPortalRequestLog = z.infer<typeof insertPortalRequestLogSchema>;
export type PortalRequestLog = typeof portalRequestLogs.$inferSelect;

// Authority type codes and descriptions mapping
// Used for watchlist creation and email merge fields
export const AUTHORITY_TYPE_DESCRIPTIONS: Record<string, string> = {
  "BHO": "Broker, arranging for transportation of household goods",
  "BPH": "Broker in arranging for transportation of freight (including household goods)",
  "BPO": "Broker, arranging for transportation of freight (except household goods)",
  "CHO": "Common carrier of household goods",
  "CMH": "Common carrier of property, including household goods",
  "CMO": "Common carrier of property (except household goods)",
  "CPA": "Common carrier of passengers by motor vehicle",
  "CPC": "Common carrier of passengers, in charter and special operations",
  "CPN": "Common carrier of passengers (nonrecipient of government)",
  "CPR": "Common carrier of passengers",
  "CX2": "Certificate of Registration",
  "ENO": "US based enterprise owned/controlled by persons of Mexico",
  "NC/A": "Name change / Reentitlement, active authority",
  "NC/I": "Name change / Reentitlement, inactive authority",
  "PFB": "Freight forwarder of property, including household goods",
  "PFO": "Freight forwarder of household goods",
  "PFP": "Freight forwarder of property (except household goods)",
  "PHO": "Contract carrier of household goods",
  "PMH": "Contract carrier of property, including household goods",
  "PMO": "Contract carrier of property (except household goods)",
  "PPO": "Contract carrier of passengers",
  "REI": "Reinstatement of authority",
};

export function getAuthorityTypeDescription(code: string | null | undefined): string {
  if (!code) return "";
  return AUTHORITY_TYPE_DESCRIPTIONS[code] || code;
}

// Short authority and cargo label mapping for the Service Bureau Portal
// Codes sourced from PRD Section 3A — used to display compact carrier authority rows
export const CARGO_SHORT_MAP: Record<string, { authorityLabel: string; cargoLabel: string }> = {
  "CMO": { authorityLabel: "Common Carrier",   cargoLabel: "Property (excl. HHG)" },
  "CMH": { authorityLabel: "Common Carrier",   cargoLabel: "Property (incl. HHG)" },
  "BHO": { authorityLabel: "Broker",           cargoLabel: "Household Goods" },
  "BPH": { authorityLabel: "Broker",           cargoLabel: "Freight (incl. HHG)" },
  "BPO": { authorityLabel: "Broker",           cargoLabel: "Freight (excl. HHG)" },
  "CHO": { authorityLabel: "Common Carrier",   cargoLabel: "Household Goods" },
  "PHO": { authorityLabel: "Contract Carrier", cargoLabel: "Household Goods" },
  "PMH": { authorityLabel: "Contract Carrier", cargoLabel: "Property (incl. HHG)" },
  "PMO": { authorityLabel: "Contract Carrier", cargoLabel: "Property (excl. HHG)" },
  "PPO": { authorityLabel: "Contract Carrier", cargoLabel: "Passengers" },
  "PFB": { authorityLabel: "Freight Forwarder", cargoLabel: "Property (incl. HHG)" },
  "PFO": { authorityLabel: "Freight Forwarder", cargoLabel: "Household Goods" },
  "PFP": { authorityLabel: "Freight Forwarder", cargoLabel: "Property (excl. HHG)" },
  "CPR": { authorityLabel: "Common Carrier",   cargoLabel: "Passengers" },
  "CPC": { authorityLabel: "Common Carrier",   cargoLabel: "Passengers (Charter)" },
  "CPN": { authorityLabel: "Common Carrier",   cargoLabel: "Passengers (Non-Govt)" },
  "CPA": { authorityLabel: "Common Carrier",   cargoLabel: "Passengers (Motor Vehicle)" },
  "ENO": { authorityLabel: "Enterprise",       cargoLabel: "Mexico-owned" },
  // REI, NC/A, NC/I, CX2 intentionally omitted — these letter types have no carrier/cargo distinction
};

export function getCargoShortInfo(code: string): { authorityLabel: string; cargoLabel: string } | undefined {
  return CARGO_SHORT_MAP[code];
}

// ---------------------------------------------------------------------------
// TypeScript union types for descriptive varchar entity_type / reference_type
// columns. NOT enforced by pgEnum (PRD-04 v2 decisions D4, D4b, D4c).
// Zod validation at HTTP boundaries uses z.enum([...]) derived from these.
// ---------------------------------------------------------------------------

/**
 * notes.entity_type — recommended values.
 * Extend when adding notes support for new entity types.
 * Validated via Zod at POST /api/notes (HTTP boundary).
 */
export type NotesEntityType =
  | "work_order"
  | "company"
  | "contact"
  | "task"
  | "carrier";

/** Zod schema derived from NotesEntityType — used at HTTP boundaries. */
export const notesEntityTypeSchema = z.enum([
  "work_order",
  "company",
  "contact",
  "task",
  "carrier",
]);

/**
 * audit_trail.entity_type — recommended values.
 * Mixes Drizzle table names and Stripe object types. Includes legacy
 * singular/plural inconsistency ("contact" vs "contacts") preserved for
 * historical rows. Internal storage writes use this as a TypeScript
 * constraint only — no runtime Zod on audit-trail inserts.
 */
export type AuditTrailEntityType =
  | "user"
  | "company"
  | "carrier"
  | "work_order"
  | "task"
  | "contact"
  | "contacts"            // legacy singular/plural inconsistency, preserved
  | "contact_companies"
  | "document_request"
  | "note"
  | "invoice_line_item"   // PRD-02 audit writes
  | "stripe_invoice"
  | "stripe_payment_intent"
  | "stripe_charge";

/**
 * workOrderInvoiceLineItems.reference_type — documents the polymorphism of
 * reference_id (points to either service_rates or pass_through_fees).
 * NOT enforced by pgEnum (PRD-04 v2 D4c). Null on rows predating PRD-04.
 */
export type InvoiceLineReferenceType = "service_rate" | "pass_through_fee";

// varchar + TS union pattern per PRD-04 v2; no pgEnum (PROJECT-STATE.md standard for enum-like columns).
export type WebhookSource = "helix" | "stripe";

/**
 * Idempotency cache for incoming webhooks. Prevents replay of captured
 * signed payloads. Composite PK (source, eventId) ensures per-source
 * uniqueness without cross-source collisions.
 *
 * TTL: rows older than 30 days are cleaned up by a weekly interval
 * registered at server startup (production-only, see server/index.ts).
 */
export const webhookIdempotency = pgTable("webhook_idempotency", {
  source: varchar("source").$type<WebhookSource>().notNull(),
  eventId: varchar("event_id").notNull(),
  receivedAt: timestamp("received_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  primaryKey({ columns: [table.source, table.eventId] }),
]);

// MCP OAuth. The app is the authorization server for /api/mcp.
// user_id columns are plain varchar with no FK, matching the MOTUS tables.
export const mcpOauthClients = pgTable("mcp_oauth_clients", {
  id: serial("id").primaryKey(),
  clientId: text("client_id").notNull().unique(),
  clientName: text("client_name").notNull(),
  redirectUris: jsonb("redirect_uris").$type<string[]>().notNull(),
  metadataUrl: text("metadata_url"),
  fetchedAt: timestamp("fetched_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastUsedAt: timestamp("last_used_at"),
});

export const mcpOauthRequests = pgTable(
  "mcp_oauth_requests",
  {
    id: text("id").primaryKey(),
    clientId: text("client_id").notNull(),
    redirectUri: text("redirect_uri").notNull(),
    state: text("state"),
    codeChallenge: text("code_challenge").notNull(),
    resource: text("resource").notNull(),
    scope: text("scope"),
    status: text("status").notNull().default("pending"),
    userId: varchar("user_id"),
    userName: text("user_name"),
    codeHash: text("code_hash").unique(),
    codeExpiresAt: timestamp("code_expires_at"),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [index("mcp_oauth_requests_expires_at_idx").on(table.expiresAt)],
);

export const mcpOauthTokens = pgTable(
  "mcp_oauth_tokens",
  {
    id: serial("id").primaryKey(),
    tokenHash: text("token_hash").notNull().unique(),
    kind: text("kind").notNull(),
    familyId: text("family_id").notNull(),
    clientId: text("client_id").notNull(),
    userId: varchar("user_id").notNull(),
    userName: text("user_name"),
    resource: text("resource").notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    revokedAt: timestamp("revoked_at"),
    usedAt: timestamp("used_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("mcp_oauth_tokens_family_id_idx").on(table.familyId),
    index("mcp_oauth_tokens_user_id_idx").on(table.userId),
  ],
);

export const mcpAuditLog = pgTable(
  "mcp_audit_log",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id").notNull(),
    clientId: text("client_id"),
    toolName: text("tool_name").notNull(),
    args: jsonb("args").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [index("mcp_audit_log_user_id_created_at_idx").on(table.userId, table.createdAt)],
);

export type McpOauthClient = typeof mcpOauthClients.$inferSelect;
export type McpOauthRequest = typeof mcpOauthRequests.$inferSelect;
export type McpOauthToken = typeof mcpOauthTokens.$inferSelect;

// Generated by an AI coding agent on 2026-10-30.
// Raw inbound requests stay separate from companies until staff qualifies them.
export const landingLeads = pgTable("landing_leads", {
  eventId: varchar("event_id", { length: 36 }).primaryKey(),
  // Sending site, e.g. dna-landing. GetMyMCLetter.com can share this table later.
  source: varchar("source", { length: 40 }).notNull(),
  receivedAt: timestamp("received_at", { withTimezone: true }).defaultNow().notNull(),
  submittedAt: timestamp("submitted_at", { withTimezone: true }).notNull(),
  name: varchar("name", { length: 80 }).notNull(),
  phone: varchar("phone", { length: 16 }).notNull(),
  email: varchar("email", { length: 254 }).notNull(),
  usdot: varchar("usdot", { length: 8 }).notNull(),
  noUsdot: boolean("no_usdot").notNull(),
  interest: varchar("interest", { length: 200 }).notNull(),
  offerSlug: varchar("offer_slug", { length: 40 }).notNull(),
  attribution: jsonb("attribution").$type<Record<string, string>>().notNull(),
}, table => [index("idx_landing_leads_received_at").on(table.receivedAt)]);
